/**
 * Where the access token lives.
 *
 * The Dawn Patrol API authenticates with `Authorization: Bearer <token>`
 * (passport-http-bearer on the Express side; the GraphQL handler reads the same
 * header). It does not use cookies, so there is nothing the browser attaches
 * for us — every request has to carry the header itself.
 *
 * **`sessionStorage`, per tab.** The token survives a reload in the same tab
 * and is gone when the tab closes (ticket 825). It is never written to
 * `localStorage`, a cookie or a URL. Holding it in script-readable storage is
 * an accepted XSS trade-off: the CSP is the mitigation, so `script-src` must
 * not be widened casually. `signOutReason` is not stored — it stays in memory
 * in `SessionProvider`.
 *
 * Storage is read on every call, so a fresh page load sees the previous
 * load's token. If any storage call throws (sandboxed iframe, private mode),
 * the store falls back to a module variable for the rest of the page's life;
 * the `storageBroken` flag stops reads and writes drifting apart after that.
 */

/** Exported so tests can arrange or inspect a "previous page load". */
export const ACCESS_TOKEN_STORAGE_KEY = 'dawnpatrol.accessToken';

let memoryToken: string | null = null;
let storageBroken = false;

type Listener = (token: string | null) => void;
const listeners = new Set<Listener>();

function normalise(value: string | null): string | null {
    return value !== null && value.trim() !== '' ? value : null;
}

export function getAccessToken(): string | null {
    if (!storageBroken) {
        try {
            return normalise(
                globalThis.sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)
            );
        } catch {
            storageBroken = true;
        }
    }

    return normalise(memoryToken);
}

export function setAccessToken(token: string | null): void {
    const next = normalise(token);

    if (!storageBroken) {
        try {
            if (next === null) {
                globalThis.sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
            } else {
                globalThis.sessionStorage.setItem(
                    ACCESS_TOKEN_STORAGE_KEY,
                    next
                );
            }
        } catch {
            storageBroken = true;
        }
    }

    // Always mirror into memory: it is the source of truth once storage has
    // failed, and harmless while it has not (reads prefer storage).
    memoryToken = next;

    for (const listener of listeners) {
        listener(next);
    }
}

export function clearAccessToken(): void {
    setAccessToken(null);
}

/** Subscribe to token changes. Returns an unsubscribe function. */
export function onAccessTokenChange(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}
